/**
 * Infinite collection scrolling with a complete link-based fallback.
 *
 * The server-rendered pagination is left alone until this script has found a
 * valid grid and next-page link. No history entries are written: product links
 * retain their normal destinations and Back returns to the collection URL the
 * shopper actually visited.
 */

(() => {
  const initInfiniteScroll = (root) => {
    if (root.dataset.infiniteScrollBound === 'true') return;

    const grid = root.querySelector('[data-infinite-grid]');
    const controls = root.querySelector('[data-infinite-controls]');
    const pagination = root.querySelector('[data-infinite-pagination]');
    const status = root.querySelector('[data-infinite-status]');
    const sentinel = root.querySelector('[data-infinite-sentinel]');
    let nextLink = root.querySelector('[data-infinite-next]');

    // If there is nothing to enhance, native pagination remains untouched.
    // A direct visit to page 2+ must retain the native Previous link. Infinite
    // scrolling begins on page 1 only, where hiding pagination loses nothing.
    // Browsers without IntersectionObserver also retain native pagination.
    if (
      !grid
      || !controls
      || !nextLink
      || !sentinel
      || !('IntersectionObserver' in window)
      || Number(controls.dataset.currentPage || 1) !== 1
    ) return;

    root.dataset.infiniteScrollBound = 'true';
    controls.classList.add('infinite-scroll--automatic');
    controls.hidden = false;
    nextLink.hidden = true;
    if (pagination) pagination.hidden = true;

    const loadedUrls = new Set();
    const itemKeys = new Set();
    let productsShown = Number(controls.dataset.productsShown || grid.children.length);
    const productsTotal = Number(controls.dataset.productsTotal || productsShown);
    let isLoading = false;
    let ajaxFailed = false;
    let observer;

    const keyFor = (item) => {
      if (item.dataset.productId) return `product:${item.dataset.productId}`;
      const productLink = item.querySelector('a[href*="/products/"]');
      return productLink ? `url:${new URL(productLink.href, window.location.href).pathname}` : '';
    };

    grid.querySelectorAll('[data-infinite-item]').forEach((item) => {
      const key = keyFor(item);
      if (key) itemKeys.add(key);
    });

    const absoluteUrl = (url) => new URL(url, window.location.href).href;

    const findMatchingRoot = (page) => {
      const sectionId = root.dataset.infiniteSectionId;
      return [...page.querySelectorAll('[data-infinite-scroll]')].find(
        (candidate) => candidate.dataset.infiniteSectionId === sectionId,
      );
    };

    const announce = (message) => {
      if (status) status.textContent = message;
    };

    const setIdleButton = (label = 'Continue to next page') => {
      if (!nextLink) return;
      nextLink.textContent = label;
      nextLink.removeAttribute('aria-busy');
      nextLink.removeAttribute('aria-disabled');
    };

    const finish = () => {
      observer?.disconnect();
      if (sentinel) sentinel.hidden = true;
      if (nextLink) {
        nextLink.hidden = true;
        nextLink.removeAttribute('aria-busy');
      }
      announce(`All ${productsTotal || productsShown} products loaded.`);
    };

    const loadNextPage = async (event) => {
      // After one AJAX failure this stays a normal anchor. A shopper can click
      // it again to use the server-rendered next page instead of being trapped
      // in a retry loop.
      if (event && ajaxFailed) return;
      event?.preventDefault();
      if (isLoading || !nextLink) return;

      const targetUrl = absoluteUrl(nextLink.href);
      if (loadedUrls.has(targetUrl)) {
        finish();
        return;
      }

      isLoading = true;
      loadedUrls.add(targetUrl);
      nextLink.setAttribute('aria-busy', 'true');
      nextLink.setAttribute('aria-disabled', 'true');
      nextLink.textContent = 'Loading…';
      root.setAttribute('aria-busy', 'true');
      announce('Loading more products…');

      try {
        const response = await window.fetch(targetUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`Request failed with ${response.status}`);

        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextRoot = findMatchingRoot(page);
        if (!nextRoot) throw new Error('Collection section was missing from the response');

        const incomingItems = [...nextRoot.querySelectorAll('[data-infinite-grid] > [data-infinite-item]')];
        if (!incomingItems.length) throw new Error('No product cards were returned');

        const newItems = incomingItems.filter((item) => {
          const key = keyFor(item);
          if (key && itemKeys.has(key)) return false;
          if (key) itemKeys.add(key);
          return true;
        });

        if (!newItems.length) throw new Error('The next page only contained products already shown');

        const fragment = document.createDocumentFragment();
        newItems.forEach((item) => fragment.appendChild(item));
        grid.appendChild(fragment);
        productsShown += newItems.length;

        root.dispatchEvent(new CustomEvent('msp:products:appended', {
          bubbles: true,
          detail: { container: grid, items: newItems },
        }));

        const incomingNext = nextRoot.querySelector('[data-infinite-next]');
        if (incomingNext) {
          nextLink.href = incomingNext.getAttribute('href');
          ajaxFailed = false;
          setIdleButton();
          announce(`Loaded ${newItems.length} more products. Showing ${productsShown} of ${productsTotal}.`);
        } else {
          finish();
        }
      } catch (_error) {
        // Preserve the real destination and stop observing. The next manual
        // click is allowed through as ordinary browser navigation.
        loadedUrls.delete(targetUrl);
        ajaxFailed = true;
        observer?.disconnect();
        controls.classList.remove('infinite-scroll--automatic');
        nextLink.hidden = false;
        setIdleButton('Continue to next page');
        announce('Could not load more products automatically. Continue to the next page.');
      } finally {
        isLoading = false;
        root.removeAttribute('aria-busy');
      }
    };

    nextLink.addEventListener('click', loadNextPage);

    observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadNextPage();
    }, { rootMargin: '500px 0px' });
    observer.observe(sentinel);
  };

  const initializeWithin = (scope) => {
    if (scope.matches?.('[data-infinite-scroll]')) initInfiniteScroll(scope);
    scope.querySelectorAll?.('[data-infinite-scroll]').forEach(initInfiniteScroll);
  };

  initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
